import { createContext, useContext, ReactNode, useCallback } from 'react';
import { Toast } from '@base-ui-components/react/toast';

interface ToastContextType {
  success: (message: string, title?: string) => void;
  error: (message: string, title?: string) => void;
  warning: (message: string, title?: string) => void;
  info: (message: string, title?: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider = ({ children }: ToastProviderProps) => {
  return (
    <Toast.Provider>
      <ToastProviderInner>{children}</ToastProviderInner>
      <Toast.Portal>
        <Toast.Viewport className="fixed z-[9999] top-[calc(env(safe-area-inset-top,0px)+16px)] right-0 bottom-auto left-0 mx-auto flex w-full max-w-[300px] sm:max-w-[360px]">
          <ToastList />
        </Toast.Viewport>
      </Toast.Portal>
    </Toast.Provider>
  );
};

function ToastProviderInner({ children }: ToastProviderProps) {
  const toastManager = Toast.useToastManager();

  const showToast = useCallback(
    (message: string, title: string, type: 'success' | 'error' | 'warning' | 'info') => {
      toastManager.add({
        title,
        description: message,
        type,
        timeout: 2000, // 30秒后自动关闭
      });
    },
    [toastManager]
  );

  const success = useCallback(
    (message: string, title: string = '成功') => {
      showToast(message, title, 'success');
    },
    [showToast]
  );

  const error = useCallback(
    (message: string, title: string = '错误') => {
      showToast(message, title, 'error');
    },
    [showToast]
  );

  const warning = useCallback(
    (message: string, title: string = '警告') => {
      showToast(message, title, 'warning');
    },
    [showToast]
  );

  const info = useCallback(
    (message: string, title: string = '提示') => {
      showToast(message, title, 'info');
    },
    [showToast]
  );

  const toast: ToastContextType = { success, error, warning, info };

  return <ToastContext.Provider value={toast}>{children}</ToastContext.Provider>;
}

function ToastList() {
  const { toasts } = Toast.useToastManager();

  const iconColors = {
    success: 'text-green-500',
    error: 'text-red-500',
    warning: 'text-amber-500',
    info: 'text-blue-500',
  };

  const icons = {
    success: '✓',
    error: '✕',
    warning: '⚠',
    info: 'ℹ',
  };

  return toasts.map((toast) => {
    const type = (toast.type || 'info') as keyof typeof icons;
    return (
      <Toast.Root
        key={toast.id}
        toast={toast}
        swipeDirection="up"
        className="[--gap:0.75rem] [--peek:0.75rem] [--scale:calc(max(0,1-(var(--toast-index)*0.1)))] [--shrink:calc(1-var(--scale))] [--height:var(--toast-frontmost-height,var(--toast-height))] [--offset-y:calc(var(--toast-offset-y)+(var(--toast-index)*var(--gap))+var(--toast-swipe-movement-y))] absolute right-0 left-0 top-0 z-[calc(1000-var(--toast-index))] mx-auto w-full max-w-[300px] origin-top rounded-xl border border-gray-200 bg-white p-4 shadow-lg sm:max-w-[360px] data-[behind]:pointer-events-none data-[expanded]:pointer-events-auto [transform:translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--toast-swipe-movement-y)+(var(--toast-index)*var(--peek))+(var(--shrink)*var(--height))))_scale(var(--scale))] after:absolute after:bottom-full after:left-0 after:h-[calc(var(--gap)+1px)] after:w-full after:content-[''] data-[expanded]:[transform:translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--offset-y)))] data-[limited]:opacity-0 data-[ending-style]:opacity-0 data-[starting-style]:[transform:translateY(-150%)] [&[data-ending-style]:not([data-limited]):not([data-swipe-direction])]:[transform:translateY(-150%)] data-[ending-style]:data-[swipe-direction=down]:[transform:translateY(calc(var(--toast-swipe-movement-y)+150%))] data-[expanded]:data-[ending-style]:data-[swipe-direction=down]:[transform:translateY(calc(var(--toast-swipe-movement-y)+150%))] data-[ending-style]:data-[swipe-direction=left]:[transform:translateX(calc(var(--toast-swipe-movement-x)-150%))_translateY(var(--offset-y))] data-[expanded]:data-[ending-style]:data-[swipe-direction=left]:[transform:translateX(calc(var(--toast-swipe-movement-x)-150%))_translateY(var(--offset-y))] data-[ending-style]:data-[swipe-direction=right]:[transform:translateX(calc(var(--toast-swipe-movement-x)+150%))_translateY(var(--offset-y))] data-[expanded]:data-[ending-style]:data-[swipe-direction=right]:[transform:translateX(calc(var(--toast-swipe-movement-x)+150%))_translateY(var(--offset-y))] data-[ending-style]:data-[swipe-direction=up]:[transform:translateY(calc(var(--toast-swipe-movement-y)-150%))] data-[expanded]:data-[ending-style]:data-[swipe-direction=up]:[transform:translateY(calc(var(--toast-swipe-movement-y)-150%))] h-[var(--height)] data-[expanded]:h-[var(--toast-height)] [transition:transform_0.45s_cubic-bezier(0.22,1,0.36,1),opacity_0.45s,height_0.18s]"
      >
        <Toast.Content className="overflow-hidden transition-opacity [transition-duration:250ms] data-[behind]:pointer-events-none data-[behind]:opacity-0 data-[expanded]:pointer-events-auto data-[expanded]:opacity-100">
          <div className="flex items-start gap-3">
            <div className={`${iconColors[type]} text-xl font-bold flex-shrink-0`}>{icons[type]}</div>
            <div className="flex-1">
              <Toast.Title className="mb-1 text-sm font-semibold text-gray-900" />
              <Toast.Description className="text-xs text-gray-600" />
            </div>
            <Toast.Close
              className="flex h-5 w-5 items-center justify-center rounded border-none bg-transparent text-gray-500 transition hover:bg-gray-100 hover:text-gray-700"
              aria-label="关闭"
            >
              <XIcon className="h-4 w-4" />
            </Toast.Close>
          </div>
        </Toast.Content>
      </Toast.Root>
    );
  });
}

function XIcon(props: React.ComponentProps<'svg'>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

/**
 * 使用 Toast 的 Hook
 */
export const useToast = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
